import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { File as IFile } from '@shared/api/api-loop/models/file';
import { AttachmentService } from '@shared/modules/main/common/attachments/attachment/attachment.service';
import { isImage } from '@shared/modules/files/components/pipes/is-image/is-image';
import { DropdownItem } from '@shared/ui/dropdown/interfaces/dropdown-item';
import { ElectronService } from '@shared/services/electron/electron';
import { FileModel } from '@dta/shared/models-api-loop/file.model';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'loop-chat-comment-attachment',
  templateUrl: './chat-comment-attachment.component.html',
  styleUrls: ['./chat-comment-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatCommentAttachmentComponent implements OnDestroy {
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly electronService: ElectronService = inject(ElectronService);

  private _attachment?: IFile;
  protected readonly attachment$: ReplaySubject<IFile> = new ReplaySubject<IFile>(1);

  @ViewChild('imageTpl') imageTpl?: ElementRef;

  @HostBinding('class.w-100') isNotImageLoaded: boolean = true;
  @HostBinding('class.is-image') isImageAttachment: boolean = false;
  @Input() set attachment(attachment: IFile) {
    this.isImageAttachment = isImage(attachment);
    if (!this.isImageAttachment) {
      this.isNotImageLoaded = false;
    }

    this._attachment = attachment;
    this.attachment$.next(attachment);
  }

  /** Attachments of the same comment, so the viewer opens as a gallery the user can page through. */
  @Input() siblingAttachments?: IFile[];

  /** Renders the image as one tile of a multi-image comment instead of a single full-width preview. */
  @Input() @HostBinding('class.stacked') isStacked: boolean = false;

  protected readonly imageDropdownItems: DropdownItem[] = [
    {
      title: 'Copy image',
      action: () => {
        this.electronService.saveFileToClipboard$(this._attachment).pipe(take(1), untilDestroyed(this)).subscribe();
      }
    }
  ];

  onAttachmentClick($event: MouseEvent, attachment: IFile): void {
    $event.preventDefault();
    $event.stopPropagation();

    const gallery = this.siblingAttachments?.length ? this.siblingAttachments : [attachment];
    this.attachmentService.onFileIconClick({
      file: new FileModel({ ...attachment }),
      files: gallery.map(file => new FileModel({ ...file }))
    });
  }

  downloadAttachment($event: MouseEvent, file: IFile): void {
    $event.stopPropagation();

    this.attachmentService
      .downloadAttachment$(new FileModel({ ...file }))
      .pipe(untilDestroyed(this), take(1))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.attachment$.complete();
  }
}
